import React, { useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faLock, faPencilAlt } from '@fortawesome/free-solid-svg-icons';
import CgRadixButton from 'modules/Common/ui/CgRadixButton';
import CgTableSkeleton from 'modules/Common/table/CgTableSkeleton';
import { useIsAdmin } from 'hooks/usePermissions';
import { useRoles, useRoleGrants, useUpdateRoleGrants } from '../hooks/usePermissionsAdmin';
import usePermissionsAdminStore from '../stores/permissionsAdminStore';
import { isLegacyRole, getLegacyRoleNote } from '../legacyRoles';
import RoleMatrixGrid from './RoleMatrixGrid';
import { useTranslation } from 'react-i18next';

// Panel rendered inside the Clerk OrganizationProfile dialog. The content
// column is ~640px wide; everything below intentionally uses the same type
// scale as CgDialog (h2/description) so this looks native to the Clerk
// dialog chrome rather than the bigger, page-level header style.
//
// Two views toggled by orgProfileEditingRoleSlug in the store (survives
// Clerk-driven remounts). We never history.push to /settings/permissions/*
// because that renders the old page-level editor behind/below this dialog.

function RolesPanel() {
  const { t } = useTranslation('admin');
  const isAdmin = useIsAdmin();
  const { data: roles, isLoading } = useRoles();
  const editingSlug = usePermissionsAdminStore((s) => s.orgProfileEditingRoleSlug);
  const setEditingSlug = usePermissionsAdminStore((s) => s.setOrgProfileEditingRoleSlug);

  const sortedRoles = useMemo(() => {
    if (!roles) return [];
    return [...roles].sort((a, b) => {
      if (a.isSystem !== b.isSystem) return a.isSystem ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
  }, [roles]);

  // Non-admins can browse the role list + inspect a role's matrix, but they
  // can't edit grants. Write affordances below are gated on `isAdmin`; the
  // read-only paths are always shown so anyone can see what their role
  // grants them.
  if (editingSlug) {
    return (
      <RoleEditorInline
        slug={editingSlug}
        isAdmin={isAdmin}
        onBack={() => {
          // Reset the editor draft as part of the back action instead of an
          // unmount effect — keeps store touch points imperative and avoids
          // any chance of subscribe-induced loops in the inline editor.
          usePermissionsAdminStore.getState().resetEditor();
          setEditingSlug(null);
        }}
      />
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {/* Role creation and deletion are provisioned by Carbon Glance
          (backend/Clerk), not self-serve — the panel only lists roles and
          edits their permission grants. */}
      <div>
        <h2 className="text-base font-semibold text-slate-800 m-0">{t('page.roles', 'Roles')}</h2>
        <p className="text-xs text-slate-500 mt-1 m-0 max-w-md">
          {isAdmin
            ? t(
                'page.rolesIntroEditable',
                'Roles bundle permissions and are assigned to users. Click a role to edit its permission matrix.',
              )
            : t(
                'page.rolesIntroReadOnly',
                'Roles bundle permissions and are assigned to users. Click a role to inspect its permission matrix.',
              )}
        </p>
      </div>

      {isLoading && !roles ? (
        <CgTableSkeleton
          rows={5}
          columns={isAdmin ? 3 : 2}
        />
      ) : (
        <div className="border border-slate-200 rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-slate-100">
            <thead className="bg-slate-50">
              <tr>
                <Th>{t('fields.name', 'Name')}</Th>
                <Th className="w-24 text-right">{t('page.permissions', 'Permissions')}</Th>
                {isAdmin && (
                  <Th className="w-24 text-right">{t('common:table.actions', 'Actions')}</Th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-100">
              {sortedRoles.map((role) => (
                <tr
                  key={role.slug}
                  className="hover:bg-slate-50 transition-colors"
                >
                  <td className="px-2 py-2 max-w-[20rem]">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {role.isSystem && (
                        <FontAwesomeIcon
                          icon={faLock}
                          className="w-2.5 h-2.5 text-slate-400"
                          title={t('badges.systemRole', 'System role')}
                        />
                      )}
                      <button
                        className="text-xs font-semibold text-slate-800 hover:text-primaryColor"
                        onClick={() => setEditingSlug(role.slug)}
                      >
                        {role.name}
                      </button>
                      {isLegacyRole(role.slug) && (
                        <span
                          className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-semibold"
                          title={t(
                            'badges.legacyHint',
                            'Legacy role — its effect is hard-coded in the frontend feature gates',
                          )}
                        >
                          {t('badges.legacy', 'Legacy')}
                        </span>
                      )}
                    </div>
                    {role.description && (
                      <div className="text-[10px] text-slate-500 mt-0.5 truncate">
                        {role.description}
                      </div>
                    )}
                  </td>
                  <td className="px-2 py-2 text-xs text-slate-700 text-right">
                    {role.permissionCount ?? 0}
                  </td>
                  {/* Actions column only for admins. Non-admins still get
                      into the editor via the name button above — they just
                      see a read-only matrix. Role deletion (like creation)
                      is provisioned by Carbon Glance, not self-serve. */}
                  {isAdmin && (
                    <td className="px-2 py-2">
                      <div className="flex items-center justify-end gap-1">
                        <IconBtn
                          icon={faPencilAlt}
                          title={t('actions.editPermissions', 'Edit permissions')}
                          onClick={() => setEditingSlug(role.slug)}
                        />
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * Inline matrix editor used inside the Clerk OrganizationProfile dialog.
 * Mirrors RoleEditorPage's logic (init/dirty/save) but minus the page-level
 * chrome (no DashboardLayout, no breadcrumb) and laid out vertically so the
 * Save/Discard actions don't compete with the dialog's close X.
 */
function RoleEditorInline({ slug, isAdmin, onBack }) {
  const { t } = useTranslation('admin');
  const { data: roles } = useRoles();
  const { data: grants, isLoading: grantsLoading, error: grantsError } = useRoleGrants(slug);
  const role = useMemo(() => roles?.find((r) => r.slug === slug), [roles, slug]);

  const draft = usePermissionsAdminStore((s) => s.editorDraft);
  const dirty = usePermissionsAdminStore((s) => s.editorDirty);

  const saveGrants = useUpdateRoleGrants(slug);

  // Hydrate once per slug. Read editorRoleSlug imperatively (not via a
  // selector) so initEditor updating the store can't re-trigger this effect
  // and loop — same guard RoleEditorPage uses, without subscribing.
  useEffect(() => {
    if (!grants?.permissions) return;
    if (usePermissionsAdminStore.getState().editorRoleSlug === slug) return;
    usePermissionsAdminStore.getState().initEditor(slug, grants.permissions);
  }, [slug, grants?.permissions]);

  const handleSave = async () => {
    if (!draft) return;
    const permissions = Object.keys(draft).filter((k) => draft[k]);
    await saveGrants.mutateAsync(permissions);
    usePermissionsAdminStore.getState().markEditorClean();
  };

  const handleDiscard = () => {
    if (grants?.permissions) {
      usePermissionsAdminStore.getState().initEditor(slug, grants.permissions);
    }
  };

  const isSystem = Boolean(role?.isSystem);
  const isLegacy = isLegacyRole(slug);
  const legacyNote = isLegacy ? getLegacyRoleNote(slug) : null;

  return (
    <div className="flex flex-col gap-3">
      <div>
        <button
          onClick={onBack}
          className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 mb-2"
        >
          <FontAwesomeIcon
            icon={faChevronLeft}
            className="w-2.5 h-2.5"
          />
          {t('nav.backToRoles', 'Back to roles')}
        </button>
        <div className="flex items-center gap-2 flex-wrap">
          <h2 className="text-base font-semibold text-slate-800 m-0">{role?.name || '…'}</h2>
          {isSystem && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[10px] font-semibold uppercase tracking-wider">
              <FontAwesomeIcon
                icon={faLock}
                className="w-2 h-2"
              />
              {t('badges.system', 'System')}
            </span>
          )}
          {isLegacy && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-semibold uppercase tracking-wider">
              {t('badges.legacy', 'Legacy')}
            </span>
          )}
        </div>
        {role?.description && (
          <p className="text-xs text-slate-500 mt-1 m-0 max-w-md">{role.description}</p>
        )}
      </div>

      {/* Save / Discard toolbar only renders for admins on editable roles.
          Non-admins are inspecting; legacy roles have no editable grants. */}
      {isAdmin && !isLegacy && (
        <div className="flex justify-end gap-2">
          <CgRadixButton
            variant="secondary"
            onClick={handleDiscard}
            disabled={!dirty || saveGrants.isPending}
          >
            {t('actions.discard', 'Discard')}
          </CgRadixButton>
          <CgRadixButton
            variant="primary"
            onClick={handleSave}
            disabled={!dirty || isSystem || saveGrants.isPending}
            loading={saveGrants.isPending}
            loadingLabel={t('common:actions.saving', 'Saving…')}
          >
            <span className="whitespace-nowrap">{t('actions.saveChanges', 'Save changes')}</span>
          </CgRadixButton>
        </div>
      )}

      {isLegacy && legacyNote ? (
        <div className="border border-amber-200 bg-amber-50 rounded-md px-3 py-3 text-xs text-amber-900">
          <p className="font-semibold m-0">{legacyNote.title}</p>
          <p className="mt-1.5 m-0">{legacyNote.description}</p>
          <ul className="list-disc list-inside mt-2 space-y-0.5">
            {legacyNote.lockedFeatures.map((f) => (
              <li key={f}>{f}</li>
            ))}
          </ul>
        </div>
      ) : grantsError ? (
        <div className="border border-red-200 bg-red-50 text-red-700 rounded-md px-3 py-2 text-xs">
          {t('errors.couldntLoadPermissions', "Couldn't load this role's permissions:")}{' '}
          <span className="font-mono">
            {grantsError?.message || t('errors.requestFailed', 'request failed')}
          </span>
        </div>
      ) : !draft && (grantsLoading || !grants?.permissions) ? (
        <CgTableSkeleton
          rows={8}
          columns={5}
        />
      ) : (
        // Matrix is read-only for non-admins (inspecting) and for any
        // isSystem role (system grants are fixed on the backend).
        <RoleMatrixGrid readOnly={!isAdmin || isSystem} />
      )}
    </div>
  );
}

const Th = ({ children, className = '' }) => (
  <th
    className={`px-2 py-1.5 text-left text-[10px] font-bold uppercase tracking-wider text-slate-500 ${className}`}
  >
    {children}
  </th>
);

const IconBtn = ({ icon, onClick, disabled, danger, title }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={`w-6 h-6 rounded flex items-center justify-center transition-colors ${
      disabled
        ? 'text-slate-300 cursor-not-allowed'
        : danger
          ? 'text-red-500 hover:bg-red-50'
          : 'text-slate-500 hover:bg-slate-100 hover:text-primaryColor'
    }`}
  >
    <FontAwesomeIcon
      icon={icon}
      className="w-3 h-3"
    />
  </button>
);

export default function OrgProfileRolesPanel() {
  return <RolesPanel />;
}
